import dayjs from 'dayjs';
import { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { backend } from '../../axios/backend';
import ChevronLeft from '../../components/icons/ChevronLeft';
import ChevronRightOutline from '../../components/icons/ChevronRightOutline';
import EllipsisVerticalMini from '../../components/icons/EllipsisVerticalMini';
import PrettyLoader from '../../components/PrettyLoader';
import Button from '../../components/ui/Button';
import IconButton from '../../components/ui/IconButton';
import Modal from '../../components/ui/Modal';
import TableHeadCell from '../../components/ui/Table/TableHeadCell';
import { useAppContext } from '../../context/AppContext';
import { UserType } from '../../context/reducers/useAuthReducer';
import { parseErrorMessage } from '../../utils/error-message';

const LIMIT = 10;

const AllUsers = () => {
  const { alertError, alertSuccess } = useAppContext();

  const [skip, setSkip] = useState(0);
  const [sort, setSort] = useState<{ sortBy?: string; sortDirection?: number }>({
    sortBy: 'registeredAt',
    sortDirection: 1,
  });
  const [selectedUser, setSelectedUser] = useState<(UserType & { registeredAt: Date }) | undefined>();

  const query = useQuery(
    ['all-users', skip, sort.sortBy, sort.sortDirection],
    async () =>
      (
        await backend.get<{ count: number; users: (UserType & { registeredAt: Date })[] }>('admin/users', {
          params: { limit: LIMIT, skip, sortBy: sort.sortBy, sortDirection: sort.sortDirection },
        })
      ).data,
  );

  const resetPasswordMutation = useMutation(
    (options: { id: string }) => backend.patch('admin/new-password', { userId: options.id, password: 'Test1234' }),
    {
      onSuccess: () => {
        alertSuccess('Passwort erfolgreich zurückgesetzt!');
      },
      onError: (e) => alertError(parseErrorMessage(e)),
    },
  );

  return (
    <PrettyLoader useQueryResult={query}>
      {query.data && (
        <div className="space-y-2">
          <h2 className="text-white text-l">Aktuelle Nutzer: {query.data.count}</h2>
          <div className="overflow-x-auto relative">
            <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <TableHeadCell name="id" sort={sort} onSort={setSort}>
                    ID
                  </TableHeadCell>
                  <TableHeadCell name="firstname" sort={sort} onSort={setSort}>
                    Vorname
                  </TableHeadCell>
                  <TableHeadCell name="lastname" sort={sort} onSort={setSort}>
                    Nachname
                  </TableHeadCell>
                  <TableHeadCell name="role" sort={sort} onSort={setSort}>
                    Rolle
                  </TableHeadCell>
                  <TableHeadCell name="username" sort={sort} onSort={setSort}>
                    Username
                  </TableHeadCell>
                  <TableHeadCell name="registeredAt" sort={sort} onSort={setSort}>
                    Registrierungsdatum
                  </TableHeadCell>
                  <TableHeadCell name="verified" sort={sort} onSort={setSort}>
                    Verifiziert
                  </TableHeadCell>
                  <td></td>
                </tr>
              </thead>
              {query.data?.users.map((user) => (
                <tr key={user.id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
                  <td className="py-4 px-6">{user.id}</td>
                  <td className="py-4 px-6">{user.firstname}</td>
                  <td className="py-4 px-6">{user.lastname}</td>
                  <td className="py-4 px-6">{user.role}</td>
                  <td className="py-4 px-6">{user.username}</td>
                  <td className="py-4 px-6">{dayjs(user.registeredAt).format('DD.MM.YYYY')}</td>
                  <td className="py-4 px-6">{user.verified ? 'Ja' : 'Nein'}</td>
                  <td className="py-4 px-6">
                    <EllipsisVerticalMini onClick={() => setSelectedUser(user)} />
                  </td>
                </tr>
              ))}
            </table>
          </div>
          <div className="flex flex-row-reverse">
            <div className="flex flex-row items-center space-x-2">
              <IconButton
                Icon={ChevronLeft}
                onClick={() => {
                  setSkip((skip) => skip - LIMIT);
                }}
                isDisabled={skip - LIMIT < 0}
                variant="primary-muted"
              />
              <p className="text-white ">Seite {skip / LIMIT + 1}</p>
              <IconButton
                Icon={ChevronRightOutline}
                onClick={() => {
                  setSkip((skip) => skip + LIMIT);
                }}
                isDisabled={query.data.count < skip + LIMIT}
                variant="primary-muted"
              />
            </div>
          </div>
          <Modal isOpen={!!selectedUser} onDismiss={() => setSelectedUser(undefined)}>
            <div className="space-y-2">
              <h1 className="text-white text-lg">
                {selectedUser?.firstname} {selectedUser?.lastname}
              </h1>
              <p className="text-white">{selectedUser?.username}</p>
              <Button
                onClick={() => selectedUser && resetPasswordMutation.mutate({ id: selectedUser.id })}
                isLoading={resetPasswordMutation.isLoading}
              >
                Passwort zurücksetzen
              </Button>
            </div>
          </Modal>
        </div>
      )}
    </PrettyLoader>
  );
};

export default AllUsers;
